/* eslint-disable jsx-a11y/no-autofocus */
import React from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import s from './styles.module.scss';
import {
  DAY_ORDER,
  HOURS,
  cellKey,
  cellVisual,
  dayTotal,
  hourHeaderLabel,
} from './helpers';

import type { IDayLabel, LimitsMap } from './helpers';

interface IProps {
  limits: LimitsMap;
  dayLabels: IDayLabel[];
  selDays: Record<number, boolean>;
  selHours: Record<number, boolean>;
  targetKeys: Set<string>;
  editing: string | null;
  draft: string;
  bulkValue: string;
  onToggleDay: (day: number) => void;
  onToggleHour: (hour: number) => void;
  onBeginEdit: (day: number, hour: number) => void;
  onDraftChange: (value: string) => void;
  onCommit: () => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onBulkValueChange: (value: string) => void;
  onApply: () => void;
  onClearSelection: () => void;
}

const HourGrid: React.FC<IProps> = ({
  limits,
  dayLabels,
  selDays,
  selHours,
  targetKeys,
  editing,
  draft,
  bulkValue,
  onToggleDay,
  onToggleHour,
  onBeginEdit,
  onDraftChange,
  onCommit,
  onKeyDown,
  onBulkValueChange,
  onApply,
  onClearSelection,
}) => {
  const { t } = useTranslation();

  const targetCount = targetKeys.size;
  const noun =
    targetCount === 1 ? t('queueLimits.hour') : t('queueLimits.hours');

  return (
    <>
      {targetCount > 0 && (
        <div className={s.bulkBar} data-cy="queue-limits-bulk-bar">
          <span className={s.bulkCount}>
            {t('queueLimits.selectedSummary', { count: targetCount, noun })}
          </span>
          <span className={s.bulkDivider} />
          <span className={s.bulkLabel}>{t('queueLimits.setLimitTo')}</span>
          <input
            className={s.bulkInput}
            inputMode="numeric"
            placeholder="—"
            data-cy="queue-limits-bulk-input"
            value={bulkValue}
            onChange={(e) => onBulkValueChange(e.target.value)}
          />
          <button
            type="button"
            className={s.bulkApply}
            data-cy="queue-limits-bulk-apply"
            onClick={onApply}
          >
            {t('queueLimits.applyTo', { count: targetCount, noun })}
          </button>
          <button
            type="button"
            className={s.bulkClear}
            onClick={onClearSelection}
          >
            {t('queueLimits.clearSelection')}
          </button>
        </div>
      )}

      <div className={s.card}>
        <div className={s.gridScroll}>
          <div className={s.gridInner}>
            <div className={s.gridRow}>
              <div className={s.corner}>{t('queueLimits.dayHourHeader')}</div>
              {HOURS.map((hour) => (
                <div
                  key={hour}
                  className={classNames(s.hourHeader, {
                    [s.hourHeaderSelected]: selHours[hour],
                  })}
                  onClick={() => onToggleHour(hour)}
                >
                  {hourHeaderLabel(hour)}
                </div>
              ))}
              <div className={s.totalHeader}>{t('queueLimits.total')}</div>
            </div>

            {DAY_ORDER.map((day) => {
              const dayIsSelected = !!selDays[day];
              return (
                <div className={s.gridRow} key={day}>
                  <div
                    className={classNames(s.dayHead, {
                      [s.dayHeadSelected]: dayIsSelected,
                    })}
                    data-cy={`queue-limits-day-${day}`}
                    onClick={() => onToggleDay(day)}
                  >
                    <span
                      className={classNames(s.checkbox, {
                        [s.checkboxChecked]: dayIsSelected,
                      })}
                    >
                      {dayIsSelected && <span className={s.checkMark}>✓</span>}
                    </span>
                    <span className={s.dayName}>{dayLabels[day].short}</span>
                  </div>

                  {HOURS.map((hour) => {
                    const value = limits[day]?.[hour] ?? 0;
                    const visual = cellVisual(value);
                    const key = cellKey(day, hour);
                    const isEditing = editing === key;
                    return (
                      <div
                        key={hour}
                        className={classNames(s.cell, {
                          [s.cellTargeted]: targetKeys.has(key),
                        })}
                        style={{
                          background: visual.bg,
                          color: visual.color,
                        }}
                        onClick={() => onBeginEdit(day, hour)}
                      >
                        {isEditing ? (
                          <input
                            className={s.cellInput}
                            inputMode="numeric"
                            autoFocus
                            value={draft}
                            onChange={(e) => onDraftChange(e.target.value)}
                            onKeyDown={onKeyDown}
                            onBlur={onCommit}
                            onClick={(e) => e.stopPropagation()}
                          />
                        ) : (
                          <span>{value}</span>
                        )}
                      </div>
                    );
                  })}

                  <div className={s.totalCell}>
                    {dayTotal(limits, day).toLocaleString()}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className={s.legendRow}>
        <div className={s.legend}>
          <div className={s.legendItem}>
            <span className={s.legendSwatchZero} />
            {t('queueLimits.legendNoSends')}
          </div>
          <div className={s.legendItemRamp}>
            <span className={s.legendRamp} />
            <span>{t('queueLimits.legendLowHigh')}</span>
          </div>
        </div>
        <div className={s.hint}>{t('queueLimits.gridHint')}</div>
      </div>
    </>
  );
};

export default HourGrid;
